import { useContext, useEffect, useRef, useState } from "react";

import { DaySelector } from "./components/DaySelector";
import { EmptyState } from "./components/EmptyState";
import { RowTable } from "./components/RowTable";
import { SummaryBand } from "./components/SummaryBand";
import {
  NO_SESSION_MESSAGE,
  PUBLISH_FAILED_MESSAGE,
  addRow,
  applyPublishAnswer,
  dayKey,
  dayTotalMs,
  defaultSelection,
  hasUnsentTime,
  isLanded,
  landedMs,
  publishInputs,
  readStore,
  readRows,
  recordDeletion,
  readyCount,
  removeRow,
  rowsForDay,
  setRowDate,
  setRowElapsed,
  setRowStart,
  taskSplit,
  toggleRow,
  updateRow,
  writeRows,
} from "./timesheet";
import type { RowOutcome, TaskListState, TimesheetRow } from "./timesheet";
import styles from "./TimesheetPage.module.css";

import { timesheetService } from "@/services/timesheetService";
import { SessionContext } from "@/state/session";
import { TIMESHEET_DELETED_KEY, TIMESHEET_STORAGE_KEY, mergeRows } from "@/state/timesheet";
import type { StoredTimesheetDeletion } from "@/state/timesheet";
import { ApiError } from "@/types/api";

/** The Accelo task list: on its way, failed, or here. The rows never wait on it. */
type PageState = TaskListState;

const NO_GROUPS: readonly never[] = [];
const NO_OUTCOMES: Record<string, RowOutcome> = {};
const STOPPED_MESSAGE = "Publish was stopped. Rows may still have landed.";

/** The route's own sentence from a 400 or a 502, or the page's when it gave none. */
function publishErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const message = (err.details as { message?: unknown } | undefined)?.message;
    if (typeof message === "string" && message.trim() !== "") return message;
  }
  return PUBLISH_FAILED_MESSAGE;
}

/**
 * Timesheet — timed rows the user names and ties to an Accelo task. The page
 * shows one day at a time. The rows live in `localStorage`, every day in one
 * store, and the shown day is a view of that store. Publish sends the shown
 * day's rows, and a row is marked published only when the route names it.
 */
export function TimesheetPage() {
  const [rows, setRows] = useState<TimesheetRow[]>(() => readRows());
  const [day, setDay] = useState(() => dayKey(Date.now()));
  const [now, setNow] = useState(() => Date.now());
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [publishing, setPublishing] = useState(false);
  const [outcomes, setOutcomes] = useState<Record<string, RowOutcome>>(NO_OUTCOMES);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [deletedLanded, setDeletedLanded] = useState(false);
  const [selected, setSelected] = useState<string[]>(() =>
    defaultSelection(rowsForDay(rows, day), now)
  );
  // A second press before the first render of `publishing` must not send twice.
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const wroteAfterMount = useRef(false);
  const skipRowsWrite = useRef(false);
  const deleted = useRef<StoredTimesheetDeletion[]>(readStore().deleted);
  const realDay = useRef(dayKey(now));
  const publishController = useRef<AbortController | null>(null);
  const csrfToken = useContext(SessionContext)?.csrfToken ?? null;
  // Every count, total and publish on this page reads the shown day alone.
  const shown = rowsForDay(rows, day);
  const ready = readyCount(shown);
  const total = dayTotalMs(shown, now);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The task list arrives after sign-in. The timer never waits on it.
  useEffect(() => {
    let live = true;
    timesheetService
      .getTasks()
      .then((payload) => {
        if (!live) return;
        setState(
          payload.status === "refused"
            ? { kind: "refused", message: payload.message }
            : { kind: "ready", groups: payload.groups }
        );
      })
      .catch(() => {
        if (live) setState({ kind: "error" });
      });
    return () => {
      live = false;
    };
  }, []);

  // One clock for the whole page, not one per row.
  useEffect(() => {
    const ticker = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(ticker);
  }, []);

  useEffect(() => {
    if (!wroteAfterMount.current) {
      wroteAfterMount.current = true;
      return;
    }
    if (skipRowsWrite.current) {
      skipRowsWrite.current = false;
      return;
    }
    writeRows(rows, deleted.current);
  }, [rows]);

  // Follow midnight only while the person still shows the former real day.
  useEffect(() => {
    const today = dayKey(now);
    const previous = realDay.current;
    if (today !== previous) {
      realDay.current = today;
      if (day === previous) {
        setDay(today);
        setSelected(defaultSelection(rowsForDay(rows, today), now));
      }
    }
  }, [day, now, rows]);

  // Another tab owns the storage write. This tab only merges what it reads.
  useEffect(() => {
    function handleStorage(event: StorageEvent) {
      if (event.key !== TIMESHEET_STORAGE_KEY && event.key !== TIMESHEET_DELETED_KEY) return;
      const theirs = readStore();
      const byId = new Map<string, StoredTimesheetDeletion>();
      for (const entry of [...deleted.current, ...theirs.deleted]) {
        const held = byId.get(entry.id);
        if (held === undefined || entry.at > held.at) byId.set(entry.id, entry);
      }
      deleted.current = [...byId.values()];
      setRows((mine) => {
        if (event.key === TIMESHEET_DELETED_KEY) {
          const removed = new Set(deleted.current.map(({ id }) => id));
          const next = mine.filter((row) => !removed.has(row.id));
          if (next.length === mine.length) return mine;
          skipRowsWrite.current = true;
          return next;
        }
        skipRowsWrite.current = true;
        return mergeRows(mine, theirs.rows, deleted.current);
      });
    }
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  function handleDay(next: string) {
    setDay(next);
    setSelected(defaultSelection(rowsForDay(rows, next), now));
  }

  // A new row belongs to the day on screen, not to the day of the press.
  function handleAdd() {
    const at = Date.now();
    setNow(at);
    setRows((current) => addRow(current, at, day));
  }

  // A published row is locked here as well as in its controls.
  function handleRename(id: string, name: string) {
    setRows((current) => updateRow(current, id, (row) => ({ ...row, name })));
  }

  function handleRetask(id: string, taskId: string) {
    setRows((current) => updateRow(current, id, (row) => ({ ...row, taskId })));
  }

  function handleToggle(id: string) {
    const at = Date.now();
    setNow(at);
    setRows((current) => updateRow(current, id, (row) => toggleRow(row, at)));
  }

  // The three below act on the whole store, never on the shown day alone. A
  // row moved to another day must leave this page and stay in storage.
  function handleDelete(id: string) {
    const deleting = rows.find((row) => row.id === id);
    if (deleting !== undefined && landedMs(deleting) > 0) setDeletedLanded(true);
    recordDeletion(id);
    deleted.current = readStore().deleted;
    setRows((current) => removeRow(current, id));
  }

  function handleSetDate(id: string, dated: string) {
    setRows((current) => setRowDate(current, id, dated));
  }

  function handleSetStart(id: string, hhmm: string) {
    setRows((current) => setRowStart(current, id, hhmm));
  }

  function handleSetElapsed(id: string, ms: number) {
    const at = Date.now();
    setNow(at);
    setRows((current) => setRowElapsed(current, id, ms, at));
  }

  function handleSelect(id: string, next: boolean) {
    setSelected((current) =>
      next ? [...new Set([...current, id])] : current.filter((selectedId) => selectedId !== id)
    );
  }

  function handleStop() {
    publishController.current?.abort();
    publishController.current = null;
    inFlight.current = false;
    setPublishing(false);
    setPublishError(STOPPED_MESSAGE);
  }

  async function handlePublish() {
    if (inFlight.current) return;
    if (csrfToken === null) {
      setPublishError(NO_SESSION_MESSAGE);
      return;
    }
    // Only the shown day goes. Another day's rows are untouched by this press.
    const at = Date.now();
    setNow(at);
    const sent = publishInputs(rowsForDay(rows, day), at, selected);
    if (sent.length === 0) return;
    const controller = new AbortController();
    publishController.current = controller;
    inFlight.current = true;
    setPublishing(true);
    setPublishError(null);
    try {
      const payload = await timesheetService.publish(sent, csrfToken, controller.signal);
      if (controller.signal.aborted) return;
      if (payload.status === "refused") {
        // The person did not resolve. Nothing was sent, so no row changes.
        setPublishError(
          typeof payload.message === "string" && payload.message.trim() !== ""
            ? payload.message
            : PUBLISH_FAILED_MESSAGE
        );
        return;
      }
      if (mounted.current) {
        setOutcomes(applyPublishAnswer(rows, sent, payload).outcomes);
        setRows((current) => applyPublishAnswer(current, sent, payload).rows);
      } else {
        // The person left the page mid-publish. A landed row is still recorded.
        writeRows(applyPublishAnswer(readRows(), sent, payload).rows);
      }
    } catch (err) {
      setPublishError(controller.signal.aborted ? STOPPED_MESSAGE : publishErrorMessage(err));
    } finally {
      if (publishController.current === controller) {
        publishController.current = null;
        inFlight.current = false;
        setPublishing(false);
      }
    }
  }

  // The day splits in two: work still in flight, and rows Accelo holds that are
  // finished with. A row crosses by itself as its own state changes.
  const landed = shown.filter((row) => isLanded(row, now));
  const live = shown.filter((row) => !isLanded(row, now));

  const runningCount = shown.filter((row) => row.running).length;
  const unpublishedElsewhere = rows.filter(
    (row) => row.date !== day && hasUnsentTime(row, now)
  ).length;

  return (
    <div className={styles.page}>
      <SummaryBand
        totalMs={total}
        runningCount={runningCount}
        pausedCount={shown.length - runningCount}
        segments={taskSplit(shown, now, state.kind === "ready" ? state.groups : NO_GROUPS)}
        ready={ready}
      />
      {publishError !== null && (
        // A refusal or a failed publish: the route's sentence where it gave one.
        <p className={styles.error} role="alert">
          {publishError}
        </p>
      )}
      {state.kind === "error" && (
        <p className={styles.error} role="alert">
          Could not load your Accelo tasks. Your timers keep running.
        </p>
      )}
      {state.kind === "refused" && (
        // The sentence is the route's. The page adds no wording of its own.
        <p className={styles.error} role="alert">
          {state.message}
        </p>
      )}
      <DaySelector
        day={day}
        now={now}
        onDay={handleDay}
        total={total}
        unpublishedElsewhere={unpublishedElsewhere}
      />
      {shown.length === 0 ? (
        <EmptyState onAdd={handleAdd} />
      ) : (
        <>
          <RowTable
            rows={live}
            outcomeRows={shown}
            now={now}
            tasks={state}
            outcomes={outcomes}
            deletedLanded={deletedLanded}
            publishing={publishing}
            publishReady={ready.ready}
            selected={selected}
            {...{ onSelect: handleSelect }}
            onAdd={handleAdd}
            onRename={handleRename}
            onRetask={handleRetask}
            onToggle={handleToggle}
            onPublish={() => void handlePublish()}
            onStop={handleStop}
            onDelete={handleDelete}
            onSetDate={handleSetDate}
            onSetElapsed={handleSetElapsed}
            onSetStart={handleSetStart}
          />
          {landed.length > 0 && (
            <RowTable
              variant="landed"
              rows={landed}
              now={now}
              tasks={state}
              outcomes={outcomes}
              publishing={publishing}
              onRename={handleRename}
              onRetask={handleRetask}
              onToggle={handleToggle}
              onDelete={handleDelete}
              onSetDate={handleSetDate}
              onSetElapsed={handleSetElapsed}
              onSetStart={handleSetStart}
            />
          )}
        </>
      )}
    </div>
  );
}
